import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

import ObtieneDatosCookies from "../obtieneDatosCookiesUsuario";

// ============================================================
// ENDPOINT
// CAMBIAR SOLAMENTE SI TU RUTA ES OTRA
// ============================================================

const API_URL =
  "https://wwwa.isepsantafe.net.ar/expedientes/Ncadetes/devuelveTodasSanciones";

// ============================================================
// HELPERS
// ============================================================

const texto = (valor) =>
  String(valor ?? "").trim();

const numero = (valor) => {
  const n = Number(valor);

  return Number.isFinite(n)
    ? n
    : 0;
};

const fechaSimple = (valor) => {
  if (!valor) {
    return "";
  }

  return String(valor).substring(0, 10);
};

const fechaArgentina = (valor) => {
  if (!valor) {
    return "";
  }

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) {
    return fechaSimple(valor);
  }

  return fecha.toLocaleDateString("es-AR");
};

const fechaHoraArgentina = (valor) => {
  if (!valor) {
    return "";
  }

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) {
    return String(valor);
  }

  return fecha.toLocaleString("es-AR");
};

// ============================================================
// CANTIDAD EFECTIVA
//
// Prioridad:
// 1. resolución del recurso
// 2. cantidad confirmada
// 3. cantidad original
// ============================================================

const cantidadEfectiva = (sancion) => {
  if (
    sancion?.cantidadResuelveRecurso !== null &&
    sancion?.cantidadResuelveRecurso !== undefined &&
    sancion?.cantidadResuelveRecurso !== ""
  ) {
    return numero(
      sancion.cantidadResuelveRecurso
    );
  }

  if (
    sancion?.cantidadConfirma !== null &&
    sancion?.cantidadConfirma !== undefined &&
    sancion?.cantidadConfirma !== ""
  ) {
    return numero(
      sancion.cantidadConfirma
    );
  }

  return numero(
    sancion?.cantidad
  );
};

// ============================================================
// PROMOCIÓN
// ============================================================

const devuelvePromocion = (sancion) => {
  const promociones =
    sancion?.cadete?.Promocions;

  if (
    !Array.isArray(promociones) ||
    promociones.length === 0
  ) {
    return "";
  }

  const activa =
    promociones.find(
      (item) =>
        item?.CadetePromocion?.activo === true
    ) || promociones[0];

  return texto(
    activa?.nombre
  );
};

// ============================================================
// COMPAÑÍA
// ============================================================

const devuelveCompania = (sancion) =>
  texto(
    sancion?.cadete
      ?.Seccion
      ?.Compania
      ?.nombre
  );

// ============================================================
// SECCIÓN
// ============================================================

const devuelveSeccion = (sancion) =>
  texto(
    sancion?.cadete
      ?.Seccion
      ?.nombre
  );

// ============================================================
// CADETE
// ============================================================

const devuelveCadeteNombre = (
  sancion
) =>
  `${texto(
    sancion?.cadete?.cadeteApellido
  )} ${texto(
    sancion?.cadete?.cadeteNombre
  )}`.trim();

// ============================================================
// USUARIO SANCIONA
// ============================================================

const devuelveUsuarioSanciona = (
  sancion
) => {
  const usuario =
    sancion?.usuariosRelacionados
      ?.idUsuarioSanciona ||
    sancion?.usuarioIdUsuarioSanciona;

  if (usuario) {
    return `${texto(
      usuario.apellido
    )} ${texto(
      usuario.nombre
    )}`.trim();
  }

  return texto(
    sancion?.usuarioSanciona
  );
};

// ============================================================
// IDENTIFICACIÓN DEL USUARIO
// ============================================================

const devuelveIdentificacionSanciona = (
  sancion
) => {
  const usuario =
    sancion?.usuariosRelacionados
      ?.idUsuarioSanciona ||
    sancion?.usuarioIdUsuarioSanciona;

  return texto(
    usuario?.identificacion
  );
};

// ============================================================
// USUARIO QUE CONTROLA
// ============================================================

const devuelveUsuarioControla = (
  sancion
) => {
  const usuario =
    sancion?.usuariosRelacionados
      ?.idUsuarioControla ||
    sancion?.usuarioIdUsuarioControla;

  if (usuario) {
    return `${texto(
      usuario.apellido
    )} ${texto(
      usuario.nombre
    )}`.trim();
  }

  return texto(
    sancion?.usuarioControla
  );
};

// ============================================================
// GENERAR AGRUPACIÓN
// ============================================================

const agrupar = (
  array,
  obtenerClave,
  obtenerCantidad = () => 1
) => {
  const mapa = new Map();

  array.forEach((item) => {
    const clave =
      texto(
        obtenerClave(item)
      ) || "Sin dato";

    const cantidad =
      obtenerCantidad(item);

    if (!mapa.has(clave)) {
      mapa.set(clave, {
        nombre: clave,
        cantidad: 0,
        amonestaciones: 0,
      });
    }

    const registro =
      mapa.get(clave);

    registro.cantidad += 1;
    registro.amonestaciones +=
      numero(cantidad);
  });

  return Array.from(
    mapa.values()
  ).sort(
    (a, b) =>
      b.cantidad - a.cantidad
  );
};

// ============================================================
// COMPONENTE
// ============================================================

const InformeHistoricoSanciones = () => {
  const Usuario =
    ObtieneDatosCookies();

  const [sanciones, setSanciones] =
    useState([]);

  const [cargando, setCargando] =
    useState(false);

  const [error, setError] =
    useState("");

  // ==========================================================
  // FILTROS
  // ==========================================================

  const [busqueda, setBusqueda] =
    useState("");

  const [sede, setSede] =
    useState("");

  const [estado, setEstado] =
    useState("");

  const [tipo, setTipo] =
    useState("");

  const [promocion, setPromocion] =
    useState("");

  const [usuarioSanciona, setUsuarioSanciona] =
    useState("");

  const [fechaDesde, setFechaDesde] =
    useState("");

  const [fechaHasta, setFechaHasta] =
    useState("");

  // ==========================================================
  // SEDE DEL USUARIO
  // ==========================================================

  const sedeUsuario =
    useMemo(() => {
      if (
        Number(Usuario?.sedeID) === 2
      ) {
        return "Rosario";
      }

      if (
        Number(Usuario?.sedeID) === 3
      ) {
        return "Recreo";
      }

      return "";
    }, [Usuario?.sedeID]);

  // ==========================================================
  // CARGAR
  // ==========================================================

  const cargarSanciones =
    async () => {
      try {
        setCargando(true);
        setError("");

        console.log(
          "======================================"
        );

        console.log(
          "🚨 CARGANDO INFORME HISTÓRICO SANCIONES"
        );

        console.log(
          "Sede usuario:",
          sedeUsuario
        );

        const response =
          await axios.post(
            API_URL,
            {
              sede:
                sedeUsuario ||
                null,
            }
          );

        console.log(
          "Respuesta sanciones:",
          response.data
        );

        const datos =
          Array.isArray(
            response.data
          )
            ? response.data
            : [];

        setSanciones(datos);

        console.log(
          "Cantidad:",
          datos.length
        );

        console.log(
          "======================================"
        );
      } catch (errorConsulta) {
        console.error(
          "❌ Error cargando sanciones:",
          errorConsulta
        );

        setError(
          errorConsulta?.response?.data?.message ||
          errorConsulta?.message ||
          "No se pudieron cargar las sanciones."
        );
      } finally {
        setCargando(false);
      }
    };

  // ==========================================================
  // INICIAL
  // ==========================================================

  useEffect(() => {
    cargarSanciones();
  }, [sedeUsuario]);

  // ==========================================================
  // OPCIONES DE FILTRO
  // ==========================================================

  const sedes =
    useMemo(
      () =>
        [
          ...new Set(
            sanciones
              .map(
                (item) =>
                  texto(
                    item?.cadeteSede ||
                    item?.cadete
                      ?.cadeteSede
                  )
              )
              .filter(Boolean)
          ),
        ].sort(),
      [sanciones]
    );

  const estados =
    useMemo(
      () =>
        [
          ...new Set(
            sanciones
              .map(
                (item) =>
                  texto(
                    item?.estado
                  )
              )
              .filter(Boolean)
          ),
        ].sort(),
      [sanciones]
    );

  const tipos =
    useMemo(
      () =>
        [
          ...new Set(
            sanciones
              .map(
                (item) =>
                  texto(
                    item?.tipo
                  )
              )
              .filter(Boolean)
          ),
        ].sort(),
      [sanciones]
    );

  const promociones =
    useMemo(
      () =>
        [
          ...new Set(
            sanciones
              .map(
                devuelvePromocion
              )
              .filter(Boolean)
          ),
        ].sort(),
      [sanciones]
    );

  const usuariosSancionan =
    useMemo(
      () =>
        [
          ...new Set(
            sanciones
              .map(
                devuelveUsuarioSanciona
              )
              .filter(Boolean)
          ),
        ].sort(),
      [sanciones]
    );

  // ==========================================================
  // FILTRAR
  // ==========================================================

  const sancionesFiltradas =
    useMemo(() => {
      const buscar =
        texto(busqueda)
          .toLowerCase();

      return sanciones.filter(
        (sancion) => {
          const sedeActual =
            texto(
              sancion?.cadeteSede ||
              sancion?.cadete
                ?.cadeteSede
            );

          const estadoActual =
            texto(
              sancion?.estado
            );

          const tipoActual =
            texto(
              sancion?.tipo
            );

          const promocionActual =
            devuelvePromocion(
              sancion
            );

          const usuarioActual =
            devuelveUsuarioSanciona(
              sancion
            );

          const fecha =
            fechaSimple(
              sancion
                ?.fechaFaltaCometida
            );

          if (
            sede &&
            sedeActual !== sede
          ) {
            return false;
          }

          if (
            estado &&
            estadoActual !== estado
          ) {
            return false;
          }

          if (
            tipo &&
            tipoActual !== tipo
          ) {
            return false;
          }

          if (
            promocion &&
            promocionActual !== promocion
          ) {
            return false;
          }

          if (
            usuarioSanciona &&
            usuarioActual !== usuarioSanciona
          ) {
            return false;
          }

          if (
            fechaDesde &&
            fecha < fechaDesde
          ) {
            return false;
          }

          if (
            fechaHasta &&
            fecha > fechaHasta
          ) {
            return false;
          }

          if (buscar) {
            const contenido =
              [
                sancion?.id,
                sancion?.cadeteDni,
                sancion?.cadete
                  ?.cadeteApellido,
                sancion?.cadete
                  ?.cadeteNombre,
                sancion?.articulo,
                sancion?.inciso,
                sancion
                  ?.descripcionFalta,
                sancion?.usuarioSanciona,
                usuarioActual,
                sedeActual,
                devuelveCompania(
                  sancion
                ),
                devuelveSeccion(
                  sancion
                ),
                promocionActual,
              ]
                .join(" ")
                .toLowerCase();

            if (
              !contenido.includes(
                buscar
              )
            ) {
              return false;
            }
          }

          return true;
        }
      );
    }, [
      sanciones,
      busqueda,
      sede,
      estado,
      tipo,
      promocion,
      usuarioSanciona,
      fechaDesde,
      fechaHasta,
    ]);

  // ==========================================================
  // ESTADÍSTICAS
  // ==========================================================

  const estadisticas =
    useMemo(() => {
      const total =
        sancionesFiltradas.length;

      const cadetesUnicos =
        new Set(
          sancionesFiltradas
            .map(
              (item) =>
                item?.cadeteDni
            )
            .filter(Boolean)
        ).size;

      const totalCantidad =
        sancionesFiltradas.reduce(
          (totalAcumulado, item) =>
            totalAcumulado +
            cantidadEfectiva(item),
          0
        );

      const activas =
        sancionesFiltradas.filter(
          (item) =>
            texto(
              item?.estado
            ).toLowerCase() ===
            "activa"
        ).length;

      const confirmadas =
        sancionesFiltradas.filter(
          (item) =>
            texto(
              item?.estado
            ).toLowerCase() ===
            "confirmada"
        ).length;

      const anuladas =
        sancionesFiltradas.filter(
          (item) =>
            texto(
              item?.estado
            ).toLowerCase() ===
            "anulada"
        ).length;

      const notificadas =
        sancionesFiltradas.filter(
          (item) =>
            Boolean(
              item
                ?.fechaNotificado
            )
        ).length;

      const conDescargo =
        sancionesFiltradas.filter(
          (item) =>
            texto(
              item?.descargo
            ) !== ""
        ).length;

      const recurridas =
        sancionesFiltradas.filter(
          (item) =>
            texto(
              item?.recursa
            ) !== ""
        ).length;

      return {
        total,
        cadetesUnicos,
        totalCantidad,
        activas,
        confirmadas,
        anuladas,
        notificadas,
        conDescargo,
        recurridas,
      };
    }, [
      sancionesFiltradas,
    ]);

  // ==========================================================
  // RANKINGS
  // ==========================================================

  const porSancionador =
    useMemo(
      () =>
        agrupar(
          sancionesFiltradas,
          devuelveUsuarioSanciona,
          cantidadEfectiva
        ),
      [sancionesFiltradas]
    );

  const porCadete =
    useMemo(
      () =>
        agrupar(
          sancionesFiltradas,
          devuelveCadeteNombre,
          cantidadEfectiva
        ),
      [sancionesFiltradas]
    );

  const porSede =
    useMemo(
      () =>
        agrupar(
          sancionesFiltradas,
          (item) =>
            item?.cadeteSede ||
            item?.cadete
              ?.cadeteSede,
          cantidadEfectiva
        ),
      [sancionesFiltradas]
    );

  const porEstado =
    useMemo(
      () =>
        agrupar(
          sancionesFiltradas,
          (item) =>
            item?.estado,
          cantidadEfectiva
        ),
      [sancionesFiltradas]
    );

  const porCompania =
    useMemo(
      () =>
        agrupar(
          sancionesFiltradas,
          devuelveCompania,
          cantidadEfectiva
        ),
      [sancionesFiltradas]
    );

  const porInciso =
    useMemo(
      () =>
        agrupar(
          sancionesFiltradas,
          (item) =>
            item?.inciso,
          cantidadEfectiva
        ),
      [sancionesFiltradas]
    );

  // ==========================================================
  // LIMPIAR FILTROS
  // ==========================================================

  const limpiarFiltros = () => {
    setBusqueda("");
    setSede("");
    setEstado("");
    setTipo("");
    setPromocion("");
    setUsuarioSanciona("");
    setFechaDesde("");
    setFechaHasta("");
  };

  // ==========================================================
  // EXCEL
  // ==========================================================

  const exportarExcel = () => {
    if (
      sancionesFiltradas.length ===
      0
    ) {
      Swal.fire({
        icon: "warning",
        title: "Sin información",
        text:
          "No existen sanciones para exportar con los filtros seleccionados.",
      });

      return;
    }

    // ========================================================
    // DETALLE
    // ========================================================

    const detalle =
      sancionesFiltradas.map(
        (sancion) => ({
          ID:
            sancion?.id,

          DNI:
            sancion?.cadeteDni,

          Apellido:
            texto(
              sancion?.cadete
                ?.cadeteApellido
            ),

          Nombre:
            texto(
              sancion?.cadete
                ?.cadeteNombre
            ),

          Sede:
            texto(
              sancion?.cadeteSede ||
              sancion?.cadete
                ?.cadeteSede
            ),

          Promocion:
            devuelvePromocion(
              sancion
            ),

          Compania:
            devuelveCompania(
              sancion
            ),

          Seccion:
            devuelveSeccion(
              sancion
            ),

          Tipo:
            sancion?.tipo,

          CantidadOriginal:
            numero(
              sancion?.cantidad
            ),

          CantidadConfirmada:
            sancion?.cantidadConfirma ??
            "",

          CantidadRecurso:
            sancion
              ?.cantidadResuelveRecurso ??
            "",

          CantidadEfectiva:
            cantidadEfectiva(
              sancion
            ),

          Estado:
            sancion?.estado,

          FechaFalta:
            fechaSimple(
              sancion
                ?.fechaFaltaCometida
            ),

          Articulo:
            sancion?.articulo,

          Inciso:
            sancion?.inciso,

          Descripcion:
            sancion
              ?.descripcionFalta,

          IdUsuarioSanciona:
            sancion
              ?.idUsuarioSanciona,

          UsuarioSanciona:
            devuelveUsuarioSanciona(
              sancion
            ),

          IdentificacionUsuario:
            devuelveIdentificacionSanciona(
              sancion
            ),

          FechaNotificacion:
            fechaHoraArgentina(
              sancion
                ?.fechaNotificado
            ),

          TieneDescargo:
            texto(
              sancion?.descargo
            )
              ? "SI"
              : "NO",

          Descargo:
            sancion?.descargo ||
            "",

          FechaDescargo:
            fechaHoraArgentina(
              sancion
                ?.fechaHoraDescargo
            ),

          IdUsuarioControla:
            sancion
              ?.idUsuarioControla ??
            "",

          UsuarioControla:
            devuelveUsuarioControla(
              sancion
            ),

          ResolucionControl:
            sancion
              ?.resuelveUsuarioControla ||
            "",

          FechaConfirma:
            fechaSimple(
              sancion
                ?.fechaConfirma
            ),

          Recurso:
            sancion?.recursa ||
            "",

          FechaRecurso:
            fechaHoraArgentina(
              sancion
                ?.fechaRecursa
            ),

          ResolucionRecurso:
            sancion
              ?.resuelveRecurso ||
            "",

          UsuarioResuelveRecurso:
            sancion
              ?.usuarioResuelveRecurso ||
            "",

          SalvedadRectificada:
            sancion
              ?.salvedadRectificada ||
            "",

          FechaSalvedad:
            fechaSimple(
              sancion
                ?.fechaSalvedadRectificada
            ),

          Creada:
            fechaHoraArgentina(
              sancion?.createdAt
            ),

          Actualizada:
            fechaHoraArgentina(
              sancion?.updatedAt
            ),
        })
      );

    // ========================================================
    // RESUMEN
    // ========================================================

    const resumen = [
      {
        Indicador:
          "Total de sanciones",
        Valor:
          estadisticas.total,
      },
      {
        Indicador:
          "Cadetes sancionados",
        Valor:
          estadisticas.cadetesUnicos,
      },
      {
        Indicador:
          "Cantidad total efectiva",
        Valor:
          estadisticas.totalCantidad,
      },
      {
        Indicador:
          "Activas",
        Valor:
          estadisticas.activas,
      },
      {
        Indicador:
          "Confirmadas",
        Valor:
          estadisticas.confirmadas,
      },
      {
        Indicador:
          "Anuladas",
        Valor:
          estadisticas.anuladas,
      },
      {
        Indicador:
          "Notificadas",
        Valor:
          estadisticas.notificadas,
      },
      {
        Indicador:
          "Con descargo",
        Valor:
          estadisticas.conDescargo,
      },
      {
        Indicador:
          "Recurridas",
        Valor:
          estadisticas.recurridas,
      },
    ];

    // ========================================================
    // WORKBOOK
    // ========================================================

    const workbook =
      XLSX.utils.book_new();

    const agregarHoja = (
      nombre,
      datos
    ) => {
      const hoja =
        XLSX.utils.json_to_sheet(
          datos
        );

      // Anchos aproximados
      hoja["!cols"] = Object.keys(
        datos?.[0] || {}
      ).map(() => ({
        wch: 24,
      }));

      XLSX.utils.book_append_sheet(
        workbook,
        hoja,
        nombre
      );
    };

    agregarHoja(
      "Detalle",
      detalle
    );

    agregarHoja(
      "Resumen",
      resumen
    );

    agregarHoja(
      "Por sede",
      porSede
    );

    agregarHoja(
      "Por estado",
      porEstado
    );

    agregarHoja(
      "Por sancionador",
      porSancionador
    );

    agregarHoja(
      "Por cadete",
      porCadete
    );

    agregarHoja(
      "Por compañía",
      porCompania
    );

    agregarHoja(
      "Por inciso",
      porInciso
    );

    const fechaArchivo =
      new Date()
        .toISOString()
        .substring(0, 10);

    XLSX.writeFile(
      workbook,
      `Informe_Historico_Sanciones_${fechaArchivo}.xlsx`
    );
  };

  // ==========================================================
  // ESTILOS
  // ==========================================================

  const styles = {
    container: {
      width: "100%",
    },

    toolbar: {
      display: "flex",
      justifyContent:
        "space-between",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
      marginBottom: "18px",
    },

    title: {
      margin: 0,
      fontSize: "20px",
    },

    small: {
      color: "#6b7280",
      fontSize: "13px",
    },

    buttonPrimary: {
      border: "none",
      borderRadius: "8px",
      padding: "10px 16px",
      cursor: "pointer",
      fontWeight: 600,
      background: "#111827",
      color: "#fff",
    },

    buttonExcel: {
      border: "none",
      borderRadius: "8px",
      padding: "10px 16px",
      cursor: "pointer",
      fontWeight: 600,
      background: "#166534",
      color: "#fff",
    },

    buttonLight: {
      border:
        "1px solid #d1d5db",
      borderRadius: "8px",
      padding: "10px 16px",
      cursor: "pointer",
      background: "#fff",
    },

    buttons: {
      display: "flex",
      gap: "8px",
      flexWrap: "wrap",
    },

    filters: {
      display: "grid",
      gridTemplateColumns:
        "repeat(auto-fit, minmax(180px, 1fr))",
      gap: "10px",
      padding: "16px",
      border:
        "1px solid #e5e7eb",
      borderRadius: "12px",
      marginBottom: "18px",
      background: "#f9fafb",
    },

    input: {
      width: "100%",
      minHeight: "40px",
      padding: "8px 10px",
      border:
        "1px solid #d1d5db",
      borderRadius: "7px",
      boxSizing: "border-box",
      background: "#fff",
    },

    label: {
      display: "block",
      fontSize: "12px",
      fontWeight: 600,
      marginBottom: "5px",
      color: "#374151",
    },

    cards: {
      display: "grid",
      gridTemplateColumns:
        "repeat(auto-fit, minmax(145px, 1fr))",
      gap: "10px",
      marginBottom: "20px",
    },

    card: {
      padding: "14px",
      border:
        "1px solid #e5e7eb",
      borderRadius: "10px",
      background: "#fff",
    },

    cardLabel: {
      fontSize: "11px",
      textTransform:
        "uppercase",
      color: "#6b7280",
      fontWeight: 700,
    },

    cardNumber: {
      marginTop: "4px",
      fontSize: "25px",
      fontWeight: 700,
      color: "#111827",
    },

    rankings: {
      display: "grid",
      gridTemplateColumns:
        "repeat(auto-fit, minmax(280px, 1fr))",
      gap: "14px",
      marginBottom: "20px",
    },

    ranking: {
      border:
        "1px solid #e5e7eb",
      borderRadius: "10px",
      padding: "14px",
      background: "#fff",
    },

    rankingTitle: {
      marginTop: 0,
      marginBottom: "12px",
      fontSize: "15px",
    },

    rankingRow: {
      display: "grid",
      gridTemplateColumns:
        "30px 1fr auto",
      gap: "8px",
      padding: "8px 0",
      borderBottom:
        "1px solid #f3f4f6",
      alignItems: "center",
    },

    tableWrapper: {
      overflowX: "auto",
      border:
        "1px solid #e5e7eb",
      borderRadius: "10px",
    },

    table: {
      width: "100%",
      borderCollapse:
        "collapse",
      minWidth: "1400px",
      background: "#fff",
    },

    th: {
      textAlign: "left",
      padding: "10px",
      background: "#f3f4f6",
      borderBottom:
        "1px solid #d1d5db",
      whiteSpace: "nowrap",
      fontSize: "12px",
    },

    td: {
      padding: "9px 10px",
      borderBottom:
        "1px solid #f3f4f6",
      verticalAlign: "top",
      fontSize: "12px",
    },

    badge: {
      display:
        "inline-block",
      padding: "4px 8px",
      borderRadius: "20px",
      background: "#f3f4f6",
      fontSize: "11px",
      fontWeight: 600,
    },
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div style={styles.container}>
      {/* ===================================================== */}
      {/* CABECERA */}
      {/* ===================================================== */}

      <div style={styles.toolbar}>
        <div>
          <h3 style={styles.title}>
            Informe disciplinario histórico
          </h3>

          <div style={styles.small}>
            Análisis completo de sanciones registradas
          </div>
        </div>

        <div style={styles.buttons}>
          <button
            type="button"
            style={styles.buttonLight}
            onClick={cargarSanciones}
            disabled={cargando}
          >
            {cargando
              ? "Actualizando..."
              : "Actualizar"}
          </button>

          <button
            type="button"
            style={styles.buttonExcel}
            onClick={exportarExcel}
          >
            Excel
          </button>
        </div>
      </div>

      {/* ===================================================== */}
      {/* FILTROS */}
      {/* ===================================================== */}

      <div style={styles.filters}>
        <div>
          <label style={styles.label}>
            Buscar
          </label>

          <input
            style={styles.input}
            value={busqueda}
            onChange={(e) =>
              setBusqueda(
                e.target.value
              )
            }
            placeholder="Cadete, DNI, artículo, inciso..."
          />
        </div>

        <div>
          <label style={styles.label}>
            Desde
          </label>

          <input
            type="date"
            style={styles.input}
            value={fechaDesde}
            onChange={(e) =>
              setFechaDesde(
                e.target.value
              )
            }
          />
        </div>

        <div>
          <label style={styles.label}>
            Hasta
          </label>

          <input
            type="date"
            style={styles.input}
            value={fechaHasta}
            onChange={(e) =>
              setFechaHasta(
                e.target.value
              )
            }
          />
        </div>

        <div>
          <label style={styles.label}>
            Sede
          </label>

          <select
            style={styles.input}
            value={sede}
            onChange={(e) =>
              setSede(
                e.target.value
              )
            }
          >
            <option value="">
              Todas
            </option>

            {sedes.map(
              (item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label style={styles.label}>
            Promoción
          </label>

          <select
            style={styles.input}
            value={promocion}
            onChange={(e) =>
              setPromocion(
                e.target.value
              )
            }
          >
            <option value="">
              Todas
            </option>

            {promociones.map(
              (item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label style={styles.label}>
            Estado
          </label>

          <select
            style={styles.input}
            value={estado}
            onChange={(e) =>
              setEstado(
                e.target.value
              )
            }
          >
            <option value="">
              Todos
            </option>

            {estados.map(
              (item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label style={styles.label}>
            Tipo
          </label>

          <select
            style={styles.input}
            value={tipo}
            onChange={(e) =>
              setTipo(
                e.target.value
              )
            }
          >
            <option value="">
              Todos
            </option>

            {tipos.map(
              (item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label style={styles.label}>
            Sancionado por
          </label>

          <select
            style={styles.input}
            value={usuarioSanciona}
            onChange={(e) =>
              setUsuarioSanciona(
                e.target.value
              )
            }
          >
            <option value="">
              Todos
            </option>

            {usuariosSancionan.map(
              (item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "end",
          }}
        >
          <button
            type="button"
            style={{
              ...styles.buttonLight,
              width: "100%",
            }}
            onClick={limpiarFiltros}
          >
            Limpiar filtros
          </button>
        </div>
      </div>

      {/* ===================================================== */}
      {/* ERROR */}
      {/* ===================================================== */}

      {error && (
        <div
          style={{
            padding: "12px",
            marginBottom: "15px",
            border:
              "1px solid #fecaca",
            borderRadius: "8px",
            background: "#fef2f2",
            color: "#991b1b",
          }}
        >
          {error}
        </div>
      )}

      {/* ===================================================== */}
      {/* INDICADORES */}
      {/* ===================================================== */}

      <div style={styles.cards}>
        <div style={styles.card}>
          <div style={styles.cardLabel}>
            Sanciones
          </div>

          <div style={styles.cardNumber}>
            {estadisticas.total}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardLabel}>
            Cadetes
          </div>

          <div style={styles.cardNumber}>
            {estadisticas.cadetesUnicos}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardLabel}>
            Amonestaciones
          </div>

          <div style={styles.cardNumber}>
            {estadisticas.totalCantidad.toFixed(
              2
            )}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardLabel}>
            Activas
          </div>

          <div style={styles.cardNumber}>
            {estadisticas.activas}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardLabel}>
            Notificadas
          </div>

          <div style={styles.cardNumber}>
            {estadisticas.notificadas}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardLabel}>
            Con descargo
          </div>

          <div style={styles.cardNumber}>
            {estadisticas.conDescargo}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardLabel}>
            Recurridas
          </div>

          <div style={styles.cardNumber}>
            {estadisticas.recurridas}
          </div>
        </div>
      </div>

      {/* ===================================================== */}
      {/* RANKINGS */}
      {/* ===================================================== */}

      <div style={styles.rankings}>
        <div style={styles.ranking}>
          <h4 style={styles.rankingTitle}>
            Usuarios que más sancionan
          </h4>

          {porSancionador
            .slice(0, 10)
            .map(
              (item, index) => (
                <div
                  key={item.nombre}
                  style={styles.rankingRow}
                >
                  <strong>
                    {index + 1}
                  </strong>

                  <span>
                    {item.nombre}
                  </span>

                  <strong>
                    {item.cantidad}
                  </strong>
                </div>
              )
            )}
        </div>

        <div style={styles.ranking}>
          <h4 style={styles.rankingTitle}>
            Cadetes con más sanciones
          </h4>

          {porCadete
            .slice(0, 10)
            .map(
              (item, index) => (
                <div
                  key={item.nombre}
                  style={styles.rankingRow}
                >
                  <strong>
                    {index + 1}
                  </strong>

                  <span>
                    {item.nombre}
                  </span>

                  <strong>
                    {item.cantidad}
                  </strong>
                </div>
              )
            )}
        </div>

        <div style={styles.ranking}>
          <h4 style={styles.rankingTitle}>
            Sanciones por sede
          </h4>

          {porSede.map(
            (item, index) => (
              <div
                key={item.nombre}
                style={styles.rankingRow}
              >
                <strong>
                  {index + 1}
                </strong>

                <span>
                  {item.nombre}
                </span>

                <strong>
                  {item.cantidad}
                </strong>
              </div>
            )
          )}
        </div>

        <div style={styles.ranking}>
          <h4 style={styles.rankingTitle}>
            Compañías con más sanciones
          </h4>

          {porCompania
            .slice(0, 10)
            .map(
              (item, index) => (
                <div
                  key={item.nombre}
                  style={styles.rankingRow}
                >
                  <strong>
                    {index + 1}
                  </strong>

                  <span>
                    {item.nombre}
                  </span>

                  <strong>
                    {item.cantidad}
                  </strong>
                </div>
              )
            )}
        </div>
      </div>

      {/* ===================================================== */}
      {/* RESULTADOS */}
      {/* ===================================================== */}

      <div
        style={{
          marginBottom: "8px",
          fontSize: "13px",
          color: "#6b7280",
        }}
      >
        Mostrando{" "}
        <strong>
          {sancionesFiltradas.length}
        </strong>{" "}
        de{" "}
        <strong>
          {sanciones.length}
        </strong>{" "}
        sanciones.
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>
                Fecha
              </th>

              <th style={styles.th}>
                Cadete
              </th>

              <th style={styles.th}>
                DNI
              </th>

              <th style={styles.th}>
                Sede
              </th>

              <th style={styles.th}>
                Promoción
              </th>

              <th style={styles.th}>
                Compañía
              </th>

              <th style={styles.th}>
                Tipo
              </th>

              <th style={styles.th}>
                Cant.
              </th>

              <th style={styles.th}>
                Art.
              </th>

              <th style={styles.th}>
                Inciso
              </th>

              <th style={styles.th}>
                Sancionó
              </th>

              <th style={styles.th}>
                Estado
              </th>

              <th style={styles.th}>
                Notificada
              </th>

              <th style={styles.th}>
                Descargo
              </th>
            </tr>
          </thead>

          <tbody>
            {sancionesFiltradas.map(
              (sancion) => (
                <tr
                  key={sancion.id}
                >
                  <td style={styles.td}>
                    {fechaArgentina(
                      sancion
                        ?.fechaFaltaCometida
                    )}
                  </td>

                  <td style={styles.td}>
                    <strong>
                      {devuelveCadeteNombre(
                        sancion
                      )}
                    </strong>
                  </td>

                  <td style={styles.td}>
                    {sancion
                      ?.cadeteDni}
                  </td>

                  <td style={styles.td}>
                    {sancion
                      ?.cadeteSede ||
                      sancion
                        ?.cadete
                        ?.cadeteSede}
                  </td>

                  <td style={styles.td}>
                    {devuelvePromocion(
                      sancion
                    )}
                  </td>

                  <td style={styles.td}>
                    {devuelveCompania(
                      sancion
                    )}
                  </td>

                  <td style={styles.td}>
                    {sancion?.tipo}
                  </td>

                  <td style={styles.td}>
                    <strong>
                      {cantidadEfectiva(
                        sancion
                      )}
                    </strong>
                  </td>

                  <td style={styles.td}>
                    {sancion
                      ?.articulo}
                  </td>

                  <td
                    style={{
                      ...styles.td,
                      maxWidth:
                        "300px",
                    }}
                  >
                    {sancion?.inciso}
                  </td>

                  <td style={styles.td}>
                    {devuelveUsuarioSanciona(
                      sancion
                    )}

                    {devuelveIdentificacionSanciona(
                      sancion
                    ) && (
                      <div
                        style={
                          styles.small
                        }
                      >
                        NI:{" "}
                        {devuelveIdentificacionSanciona(
                          sancion
                        )}
                      </div>
                    )}
                  </td>

                  <td style={styles.td}>
                    <span
                      style={
                        styles.badge
                      }
                    >
                      {sancion
                        ?.estado}
                    </span>
                  </td>

                  <td style={styles.td}>
                    {sancion
                      ?.fechaNotificado
                      ? "Sí"
                      : "No"}
                  </td>

                  <td style={styles.td}>
                    {texto(
                      sancion?.descargo
                    )
                      ? "Sí"
                      : "No"}
                  </td>
                </tr>
              )
            )}

            {!cargando &&
              sancionesFiltradas.length ===
                0 && (
                <tr>
                  <td
                    colSpan={14}
                    style={{
                      ...styles.td,
                      textAlign:
                        "center",
                      padding:
                        "30px",
                    }}
                  >
                    No existen sanciones para los filtros seleccionados.
                  </td>
                </tr>
              )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InformeHistoricoSanciones;